import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { TransactionComments } from './TransactionComments';
import { LogoPicker } from './ProviderLogo';
import { US_STATES, US_STATE_CODES } from '../constants/enums';
import {
  ApprovalAPI,
  SettingsAPI,
  TransactionAPI,
  ProcessingInfo,
  ProcessingInfoUpdate,
  ProviderAccount,
  PROCESSING_ACCOUNT_STATUSES,
  PROCESSING_PAYMENT_METHODS,
  PROCESSING_UTILITY_PAYMENT_STATUSES,
  PROCESSING_SALE_STATUSES,
} from '../services/api';

// Company/processor-side detail for a BILL sale. Editable by the processing team
// (can_edit from the API); the creating sales agent sees it read-only. Sensitive
// values (SSN / passport / provider password / PIN) reveal only through the
// panel's Request Decryption flow (decryptedData); passport/password/PIN stay
// TL+ on the backend.
const ADD_PROVIDER_ROLES = ['processor_team_lead', 'floor_manager', 'director', 'admin'];
const SERVICE_DOMAINS = ['Electricity', 'Gas', 'Water'];
// The payment form only takes US addresses (country is fixed to US there).
const COUNTRIES = ['US'] as const;
// Identity / sale-detail fields saved via the sale-details endpoint (not processing-info).
const IDENTITY_FIELDS = [
  'first_name', 'middle_name', 'last_name', 'company', 'date_of_birth', 'phone', 'email',
  'address1', 'address2', 'city', 'state', 'zip', 'country',
  'drivers_license_state', 'dl_issuing_date', 'dl_expiration_date',
  'shipping_firstname', 'shipping_lastname', 'shipping_company',
  'shipping_address_1', 'shipping_address_2', 'shipping_city', 'shipping_state', 'shipping_zip', 'shipping_country',
];
// Pseudo identity: same fields as Customer identity, labelled "Pseudo …". Secret
// fields send a raw replacement under the API key; flag/last4/reveal come from
// pseudo_identity.has_<x> / <x>_last4 and the pseudo_<x> reveal field.
// Plain pseudo fields: the identity fields plus one account number.
const PSEUDO_FIELDS = [...IDENTITY_FIELDS, 'account_number'];
type PseudoSecretKey = 'ssn' | 'drivers_license_number' | 'passport_number';
const EMPTY_PSEUDO_SECRET: Record<PseudoSecretKey, string> = { ssn: '', drivers_license_number: '', passport_number: '' };
type PseudoField =
  | { key: string; label: string; kind: 'text'; max: number; placeholder?: string }
  | { key: string; label: string; kind: 'num' | 'tri' | 'state' | 'state_code' | 'country' }
  | { key: PseudoSecretKey; label: string; kind: 'secret'; flag: 'ssn' | 'dl' | 'passport' };
const PSEUDO_LAYOUT: PseudoField[] = [
  { key: 'first_name', label: 'Pseudo first name', kind: 'text', max: 64 },
  { key: 'middle_name', label: 'Pseudo middle name', kind: 'text', max: 64 },
  { key: 'last_name', label: 'Pseudo last name', kind: 'text', max: 64 },
  { key: 'company', label: 'Pseudo company', kind: 'text', max: 128 },
  { key: 'date_of_birth', label: 'Pseudo date of birth', kind: 'text', max: 10, placeholder: 'MM/DD/YYYY' },
  { key: 'phone', label: 'Pseudo phone', kind: 'text', max: 32 },
  { key: 'email', label: 'Pseudo email', kind: 'text', max: 256 },
  { key: 'account_number', label: 'Pseudo account number', kind: 'text', max: 64 },
  { key: 'sales_tax', label: 'Pseudo sales tax', kind: 'num' },
  { key: 'tax_exempt', label: 'Pseudo tax exempt', kind: 'tri' },
  { key: 'ssn', label: 'Pseudo SSN', kind: 'secret', flag: 'ssn' },
  { key: 'drivers_license_number', label: "Pseudo driver's license #", kind: 'secret', flag: 'dl' },
  { key: 'drivers_license_state', label: 'Pseudo DL issuing state', kind: 'state_code' },
  { key: 'dl_issuing_date', label: 'Pseudo DL issuing date', kind: 'text', max: 10, placeholder: 'MM/DD/YYYY' },
  { key: 'dl_expiration_date', label: 'Pseudo DL expiration date', kind: 'text', max: 10, placeholder: 'MM/DD/YYYY' },
  { key: 'passport_number', label: 'Pseudo passport', kind: 'secret', flag: 'passport' },
  { key: 'address1', label: 'Pseudo billing address 1', kind: 'text', max: 128 },
  { key: 'city', label: 'Pseudo billing city', kind: 'text', max: 64 },
  { key: 'state', label: 'Pseudo billing state', kind: 'state' },
  { key: 'zip', label: 'Pseudo billing ZIP', kind: 'text', max: 20 },
  { key: 'country', label: 'Pseudo billing country', kind: 'country' },
  { key: 'shipping_firstname', label: 'Pseudo service first name', kind: 'text', max: 64 },
  { key: 'shipping_lastname', label: 'Pseudo service last name', kind: 'text', max: 64 },
  { key: 'shipping_company', label: 'Pseudo service company', kind: 'text', max: 128 },
  { key: 'shipping_address_1', label: 'Pseudo service address 1', kind: 'text', max: 128 },
  { key: 'shipping_city', label: 'Pseudo service city', kind: 'text', max: 64 },
  { key: 'shipping_state', label: 'Pseudo service state', kind: 'state' },
  { key: 'shipping_zip', label: 'Pseudo service ZIP', kind: 'text', max: 20 },
  { key: 'shipping_country', label: 'Pseudo service country', kind: 'country' },
];
const PSEUDO_SECRET_CLEAN: Record<PseudoSecretKey, (v: string) => string> = {
  ssn: (v) => v.replace(/[^0-9]/g, '').slice(0, 9),
  drivers_license_number: (v) => v.replace(/[^A-Za-z0-9]/g, '').slice(0, 32),
  passport_number: (v) => v.replace(/[^A-Za-z0-9]/g, '').slice(0, 12),
};

interface Props {
  saleId: string;          // Transaction.id or PendingSale.id — backend resolves either
  category?: string | null;
  decryptedData?: any;     // set once the panel's Request Decryption succeeds
  reloadToken?: number;    // bump to force a refetch (e.g. after an identity edit)
  onSaved?: () => void;
  onInfo?: (info: ProcessingInfo) => void;   // every load/save (the panel header reads the Deposit flag)
}

type TriState = '' | 'Yes' | 'No';
const toTri = (v?: boolean | null): TriState => (v === true ? 'Yes' : v === false ? 'No' : '');
const fromTri = (v: TriState): boolean | null => (v === 'Yes' ? true : v === 'No' ? false : null);
// Tolerates undefined: a ticked service with no saved account details has no form values yet.
const emptyToNull = (v: string | undefined): string | null => (v == null || v.trim() === '' ? null : v);
const chList = (ch: { email: boolean; sms: boolean }): ('email' | 'sms')[] =>
  [ch.email ? 'email' : null, ch.sms ? 'sms' : null].filter(Boolean) as ('email' | 'sms')[];
const numOrNull = (v: string | undefined): number | null => (v == null || v.trim() === '' ? null : Number(v));

const errMsg = (e: any): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => (x?.msg ? `${x.msg}` : JSON.stringify(x))).join('; ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return e?.message || 'Something went wrong';
};
const isValidUrl = (v: string): boolean => /^https?:\/\/.+/i.test(v.trim());
const sanitizeMoney = (raw: string): string => {
  let v = raw.replace(/[^0-9.]/g, '');
  const i = v.indexOf('.');
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, '').slice(0, 2);
  return v;
};

export const ProcessingSection: React.FC<Props> = ({ saleId, decryptedData, reloadToken, onSaved, onInfo }) => {
  const { user } = useAuth();
  const role = user?.role || '';

  const [info, setInfo] = useState<ProcessingInfo | null>(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<Record<string, string>>({});
  const [passportInput, setPassportInput] = useState('');
  const [ssnInput, setSsnInput] = useState('');       // identity: replace SSN (raw)
  const [dlInput, setDlInput] = useState('');          // identity: replace DL number (raw)
  // Pseudo identity: plain fields (same keys as the customer identity) + raw
  // replacements for its SSN / DL # / passport (keys as the API expects them).
  const [pform, setPform] = useState<Record<string, string>>({});
  const [pSecret, setPSecret] = useState<Record<PseudoSecretKey, string>>(EMPTY_PSEUDO_SECRET);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  // Type of service (sale_domains) + per-domain provider assignment (utility_providers)
  const [domains, setDomains] = useState<string[]>([]);
  const [providerSel, setProviderSel] = useState<Record<string, string>>({});
  // Per-service account details (string form values), keyed by service
  const [acct, setAcctState] = useState<Record<string, Record<string, string>>>({});
  const [domainOptions, setDomainOptions] = useState<Record<string, { id: string; name: string }[]>>({});

  // Inform Customer (provider-branded invoice, once only)
  const [informOpen, setInformOpen] = useState(false);
  const [informProvider, setInformProvider] = useState('');
  const [informing, setInforming] = useState(false);
  const [informErr, setInformErr] = useState<string | null>(null);
  // New sales: separate "Send deposit confirmation" button (once only)
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [depositErr, setDepositErr] = useState<string | null>(null);
  // Channels ticked in each confirm step (Email / SMS). Preselected when only one is on file.
  const [informCh, setInformCh] = useState<{ email: boolean; sms: boolean }>({ email: false, sms: false });
  const [depositCh, setDepositCh] = useState<{ email: boolean; sms: boolean }>({ email: false, sms: false });

  const canEdit = !!info?.can_edit;
  // Customer identity editable inline: TL and above, or the processor agent the sale
  // is assigned to (server decides; false for view-only users).
  const canEditIdentity = canEdit && !!info?.can_edit_identity;
  // Processing-only comment thread: visible to the processing team; sales roles
  // (sales agent, sales_team_lead) have no access even if they created the sale.
  const isProcessingRole = ['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin'].includes(role);
  const canAddProvider = canEdit && ADD_PROVIDER_ROLES.includes(role);
  const decryptActive = !!decryptedData;

  const seed = useCallback((d: ProcessingInfo) => {
    setForm({
      // Customer identity / sale details (saved via the sale-details endpoint)
      first_name: d.first_name || '',
      middle_name: d.middle_name || '',
      last_name: d.last_name || '',
      company: d.company || '',
      date_of_birth: d.date_of_birth || '',
      phone: d.phone || '',
      email: d.email || '',
      sales_tax: d.sales_tax != null ? String(d.sales_tax) : '',
      tax_exempt: toTri(d.tax_exempt),
      address1: d.address1 || '',
      address2: d.address2 || '',
      city: d.city || '',
      state: d.state || '',
      zip: d.zip || '',
      country: d.country || '',
      drivers_license_state: d.drivers_license_state || '',
      dl_issuing_date: d.dl_issuing_date || '',
      dl_expiration_date: d.dl_expiration_date || '',
      shipping_firstname: d.shipping_firstname || '',
      shipping_lastname: d.shipping_lastname || '',
      shipping_company: d.shipping_company || '',
      shipping_address_1: d.shipping_address_1 || '',
      shipping_address_2: d.shipping_address_2 || '',
      shipping_city: d.shipping_city || '',
      shipping_state: d.shipping_state || '',
      shipping_zip: d.shipping_zip || '',
      shipping_country: d.shipping_country || '',
      // Processing fields
      account_number: d.account_number || '',
      provider_name: d.provider_name || '',
      provider_phone: d.provider_phone || '',
      provider_account_status: d.provider_account_status || '',
      provider_username: d.provider_username || '',
      provider_password: d.provider_password || '',
      provider_pin: d.provider_pin || '',
      guest_pay_link: d.guest_pay_link || '',
      autopay_enrolled: toTri(d.autopay_enrolled),
      total_bill_amount: d.total_bill_amount != null ? String(d.total_bill_amount) : '',
      past_due_amount: d.past_due_amount != null ? String(d.past_due_amount) : '',
      overdue_balance: d.overdue_balance != null ? String(d.overdue_balance) : '',
      next_bill_date: d.next_bill_date ? d.next_bill_date.slice(0, 10) : '',
      payment_method: d.payment_method || '',
      utility_payment_status: d.utility_payment_status || '',
      amount_collected: d.amount_collected != null ? String(d.amount_collected) : '',
      confirmation_number: d.confirmation_number || '',
      paid_at: d.paid_at ? d.paid_at.slice(0, 16) : '',
      reversal_date: d.reversal_date ? d.reversal_date.slice(0, 10) : '',
      appointment_date: d.appointment_date ? d.appointment_date.slice(0, 10) : '',
      appointment_time: d.appointment_time || '',
      cancellation_date: d.cancellation_date ? d.cancellation_date.slice(0, 10) : '',
      agent_call_flag: toTri(d.agent_call_flag),
      processor_sale_status: d.processor_sale_status || '',
    });
    setPassportInput('');
    setSsnInput('');
    setDlInput('');
    const p = d.pseudo_identity || {};
    const pf: Record<string, string> = {};
    for (const k of PSEUDO_FIELDS) pf[k] = p[k] == null ? '' : String(p[k]);
    pf.sales_tax = p.sales_tax ?? '';
    pf.tax_exempt = toTri(p.tax_exempt);
    setPform(pf);
    setPSecret(EMPTY_PSEUDO_SECRET);
    setDomains(d.sale_domains || []);
    const sel: Record<string, string> = {};
    (d.providers || []).forEach((p) => { if (p.domain && p.provider_id) sel[p.domain] = p.provider_id; });
    setProviderSel(sel);
    const ac: Record<string, Record<string, string>> = {};
    Object.entries(d.provider_accounts || {}).forEach(([svc, a]) => {
      ac[svc] = {
        account_number: a?.account_number || '',
        provider_phone: a?.provider_phone || '',
        provider_account_status: a?.provider_account_status || '',
        provider_username: a?.provider_username || '',
        provider_password: a?.provider_password || '',
        provider_pin: a?.provider_pin || '',
        guest_pay_link: a?.guest_pay_link || '',
        autopay_enrolled: toTri(a?.autopay_enrolled),
      };
    });
    setAcctState(ac);
  }, []);

  const load = useCallback(async () => {
    if (!saleId) return;
    setLoading(true);
    setError(null);
    setDenied(false);
    try {
      const d = await ApprovalAPI.getProcessingInfo(saleId);
      setInfo(d);
      seed(d);
    } catch (e: any) {
      // 403 => this viewer can't see it; render nothing rather than an error box.
      if (e?.response?.status === 403) { setDenied(true); setInfo(null); }
      else setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [saleId, seed]);

  useEffect(() => { load(); }, [load, reloadToken]);
  useEffect(() => { if (info) onInfo?.(info); }, [info, onInfo]);

  // Load the provider catalog per service type for the editable dropdowns.
  useEffect(() => {
    let active = true;
    (async () => {
      const opts: Record<string, { id: string; name: string }[]> = {};
      for (const d of SERVICE_DOMAINS) {
        try {
          const list = await SettingsAPI.getProvidersByDomain(d);
          opts[d] = (list || []).map((p: any) => ({ id: p.id, name: p.name }));
        } catch { opts[d] = []; }
      }
      if (active) setDomainOptions(opts);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!decryptActive || !info) { setRevealed({}); return; }
    (async () => {
      const out: Record<string, string> = {};
      const ssn = decryptedData?.decrypted_card_data?.ssn;
      if (ssn) out.ssn = ssn;
      try {
        const v = await ApprovalAPI.revealProcessingField(saleId, 'passport');
        if (v) out.passport = v;
      } catch { /* not permitted / not set */ }
      const p = info.pseudo_identity;
      for (const f of ['ssn', 'dl', 'passport'] as const) {
        if (!p?.[`has_${f}`]) continue;
        try {
          const v = await ApprovalAPI.revealProcessingField(saleId, `pseudo_${f}`);
          if (v) out[`pseudo_${f}`] = v;
        } catch { /* not permitted (TL+ only) */ }
      }
      if (active) setRevealed(out);
    })();
    return () => { active = false; };
  }, [decryptActive, info, saleId, decryptedData]);

  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: '' } : fe));
  };
  const acctOf = (svc: string): Record<string, string> => acct[svc] || {};
  const setAcct = (svc: string, k: string, v: string) => {
    setAcctState((a) => ({ ...a, [svc]: { ...(a[svc] || {}), [k]: v } }));
    const ek = `${svc}.${k}`;
    setFieldErrors((fe) => (fe[ek] ? { ...fe, [ek]: '' } : fe));
  };

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    const checkAmount = (k: string, label: string) => {
      const v = (form[k] || '').trim();
      if (v === '') return;
      const n = Number(v);
      if (Number.isNaN(n)) errs[k] = `${label} must be a number`;
      else if (n < 0) errs[k] = `${label} can't be negative`;
      else if (!/^\d+(\.\d{1,2})?$/.test(v)) errs[k] = `${label} allows at most 2 decimals`;
    };
    checkAmount('total_bill_amount', 'Total bill amount');
    checkAmount('past_due_amount', 'Past due amount');
    checkAmount('overdue_balance', 'Overdue balance');
    checkAmount('amount_collected', 'Actual amount collected');
    for (const svc of domains) {
      const link = (acctOf(svc).guest_pay_link || '').trim();
      if (link && !isValidUrl(link)) errs[`${svc}.guest_pay_link`] = 'Must be a valid URL (http:// or https://)';
    }
    const checkDate = (k: string, label: string) => {
      const v = (form[k] || '').trim();
      if (v && Number.isNaN(Date.parse(v))) errs[k] = `${label} is not a valid date`;
    };
    checkDate('next_bill_date', 'Next bill date');
    checkDate('reversal_date', 'Reversal date');
    checkDate('appointment_date', 'Appointment date');
    checkDate('cancellation_date', 'Cancellation date');
    checkDate('paid_at', 'Date/Time paid');
    const idVal = passportInput.trim();
    if (idVal && !/^[A-Za-z0-9]{6,12}$/.test(idVal)) errs.passport_number = 'Passport must be 6–12 letters/numbers';
    const pPass = pSecret.passport_number.trim();
    if (pPass && !/^[A-Za-z0-9]{6,12}$/.test(pPass)) errs['pseudo.passport_number'] = 'Passport must be 6–12 letters/numbers';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError('Please fix the highlighted fields.');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      // 1) Customer identity / sale details — only for roles allowed to edit them,
      // and only the fields that actually changed. Saved via the sale-details
      // endpoint (writes across the transaction / pending / link cluster).
      if (canEditIdentity) {
        const sd: Record<string, any> = {};
        for (const k of IDENTITY_FIELDS) {
          const cur = form[k] ?? '';
          const orig = (info as any)[k] == null ? '' : String((info as any)[k]);
          if (cur !== orig) sd[k] = emptyToNull(cur);
        }
        const stOrig = info?.sales_tax != null ? String(info.sales_tax) : '';
        if ((form.sales_tax ?? '') !== stOrig) sd.sales_tax = numOrNull(form.sales_tax);
        const teNew = fromTri(form.tax_exempt as TriState);
        if (teNew !== (info?.tax_exempt ?? null)) sd.tax_exempt = teNew;
        if (ssnInput.trim()) sd.ssn = ssnInput.trim();
        if (dlInput.trim()) sd.drivers_license_number = dlInput.trim();
        if (Object.keys(sd).length > 0) {
          await TransactionAPI.updateSaleDetails(saleId, sd);
        }
      }

      // 2) Processing fields.
      const payload: ProcessingInfoUpdate = {
        sale_domains: domains,
        utility_providers: domains.reduce((acc, d) => { if (providerSel[d]) acc[d] = providerSel[d]; return acc; }, {} as Record<string, string>),
        // Account details per ticked service (the server mirrors the first one into
        // the single provider fields and sets the provider name from the selection).
        provider_accounts: domains.reduce((acc, d) => {
          const a = acctOf(d);
          acc[d] = {
            account_number: emptyToNull(a.account_number),
            provider_phone: emptyToNull(a.provider_phone),
            provider_account_status: emptyToNull(a.provider_account_status),
            provider_username: emptyToNull(a.provider_username),
            provider_password: emptyToNull(a.provider_password),
            provider_pin: emptyToNull(a.provider_pin),
            guest_pay_link: emptyToNull(a.guest_pay_link),
            autopay_enrolled: fromTri((a.autopay_enrolled || '') as TriState),
          };
          return acc;
        }, {} as Record<string, ProviderAccount>),
        total_bill_amount: numOrNull(form.total_bill_amount),
        past_due_amount: numOrNull(form.past_due_amount),
        overdue_balance: numOrNull(form.overdue_balance),
        next_bill_date: emptyToNull(form.next_bill_date),
        payment_method: emptyToNull(form.payment_method),
        amount_collected: numOrNull(form.amount_collected),
        confirmation_number: emptyToNull(form.confirmation_number),
        paid_at: emptyToNull(form.paid_at),
        reversal_date: emptyToNull(form.reversal_date),
        appointment_date: emptyToNull(form.appointment_date),
        appointment_time: emptyToNull(form.appointment_time),
        cancellation_date: emptyToNull(form.cancellation_date),
        agent_call_flag: fromTri(form.agent_call_flag as TriState),
      };
      if (form.processor_sale_status && form.processor_sale_status !== (info?.processor_sale_status || '')) {
        payload.processor_sale_status = form.processor_sale_status;
      }
      // Only send Payment status when the user actually changed it, so a legacy
      // stored value (Pending/Failed/Reversed) on an untouched row is never
      // re-sent to the now-stricter validator.
      if (form.utility_payment_status !== (info?.utility_payment_status || '')) {
        payload.utility_payment_status = emptyToNull(form.utility_payment_status);
      }
      if (passportInput.trim()) payload.passport_number = passportInput.trim();
      // 3) Pseudo identity (TL and above): only the keys that changed.
      if (info?.can_edit_pseudo && info.pseudo_identity) {
        const p = info.pseudo_identity;
        const pd: Record<string, string | boolean | null> = {};
        for (const k of PSEUDO_FIELDS) {
          if ((pform[k] ?? '') !== (p[k] == null ? '' : String(p[k]))) pd[k] = emptyToNull(pform[k] ?? '');
        }
        if ((pform.sales_tax ?? '') !== (p.sales_tax ?? '')) pd.sales_tax = emptyToNull(pform.sales_tax ?? '');
        const pte = fromTri(pform.tax_exempt as TriState);
        if (pte !== (p.tax_exempt ?? null)) pd.tax_exempt = pte;
        (Object.keys(pSecret) as PseudoSecretKey[]).forEach((k) => { if (pSecret[k].trim()) pd[k] = pSecret[k].trim(); });
        if (Object.keys(pd).length > 0) payload.pseudo_identity = pd;
      }

      const updated = await ApprovalAPI.updateProcessingInfo(saleId, payload);
      setInfo(updated);
      seed(updated);
      setSavedMsg('Saved.');
      onSaved?.();
      setTimeout(() => setSavedMsg(null), 2500);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleInform = async (providerId: string) => {
    setInforming(true);
    setInformErr(null);
    try {
      const updated = await ApprovalAPI.informCustomer(saleId, providerId || undefined, chList(informCh));
      setInfo(updated);
      seed(updated);
      setInformOpen(false);
      onSaved?.();
    } catch (e: any) {
      setInformErr(e?.message || 'Failed to inform the customer');
    } finally {
      setInforming(false);
    }
  };

  const handleDeposit = async (providerId: string) => {
    setDepositing(true);
    setDepositErr(null);
    try {
      const updated = await ApprovalAPI.sendDepositConfirmation(saleId, providerId || undefined, chList(depositCh));
      setInfo(updated);
      seed(updated);
      setDepositOpen(false);
      onSaved?.();
    } catch (e: any) {
      setDepositErr(e?.message || 'Failed to send the deposit confirmation');
    } finally {
      setDepositing(false);
    }
  };

  if (denied) return null;
  if (loading && !info) return <div className="ps-muted">Loading processing details…</div>;
  if (!info) return error ? <div className="ps-error">{error}</div> : null;

  const ssnDisplay = revealed.ssn ? revealed.ssn : info.has_ssn ? `•••• ${info.ssn_last4 || ''}` : '—';
  const dlDisplay = decryptedData?.decrypted_card_data?.drivers_license_number
    ? decryptedData.decrypted_card_data.drivers_license_number
    : info.has_dl ? `•••• ${info.dl_last4 || ''}${info.drivers_license_state ? ` (${info.drivers_license_state})` : ''}` : '—';
  const bill = [info.address1, info.city, info.state, info.zip].filter(Boolean).join(', ') || '—';
  const service = [info.shipping_address_1, info.shipping_city, info.shipping_state, info.shipping_zip].filter(Boolean).join(', ') || '—';

  // Inform Customer works off SAVED values (what the customer will actually see).
  const savedProviders = (info.providers || []).filter((p, i, arr) =>
    p.provider_id && p.name && arr.findIndex((q) => q.provider_id === p.provider_id) === i);
  const savedProvMap = JSON.stringify(Object.fromEntries((info.providers || []).filter((p) => p.domain && p.provider_id).map((p) => [p.domain, p.provider_id]).sort()));
  const formProvMap = JSON.stringify(Object.fromEntries(domains.filter((d) => providerSel[d]).map((d) => [d, providerSel[d]]).sort()));
  // Which sale-type template Inform Customer sends (server decides from the category).
  const kind = info.inform_kind || null;
  const needsAppt = kind === 'restore' || kind === 'new_service' || kind === 'transfer';
  const needsCancelDate = kind === 'cancellation';
  const isNew = kind === 'new_service';
  const apptLabels = kind === 'restore' ? { date: 'Restore date', time: 'Appointment window' }
    : kind === 'transfer' ? { date: 'Transfer date', time: 'Time window' }
    : { date: 'Start date', time: 'Time window' };
  const KIND_LABEL: Record<string, string> = {
    bill: 'Bill payment confirmation', restore: 'Restoration scheduled', cancellation: 'Cancellation confirmed',
    new_service: 'New service confirmed', transfer: 'Transfer scheduled',
  };
  const savedApptDate = info.appointment_date ? info.appointment_date.slice(0, 10) : '';
  const savedCancelDate = info.cancellation_date ? info.cancellation_date.slice(0, 10) : '';
  const acctNumbersKey = (src: (svc: string) => string) => JSON.stringify(domains.map((d) => [d, src(d)]));
  const informDirty = formProvMap !== savedProvMap
    || acctNumbersKey((d) => acctOf(d).account_number || '') !== acctNumbersKey((d) => info.provider_accounts?.[d]?.account_number || '')
    || (form.amount_collected || '') !== (info.amount_collected != null ? String(info.amount_collected) : '')
    || (form.confirmation_number || '') !== (info.confirmation_number || '')
    || (form.email || '') !== (info.email || '')
    || (form.phone || '') !== (info.phone || '')
    || (needsAppt && (form.appointment_date || '') !== savedApptDate)
    || (needsAppt && (form.appointment_time || '') !== (info.appointment_time || ''))
    || (needsCancelDate && (form.cancellation_date || '') !== savedCancelDate);
  // Shared by both buttons (deposit has no date requirement).
  // Account number is optional (its line is left out of the email/SMS when blank).
  const baseBlocker = savedProviders.length === 0 ? 'Select a provider under Type of service & provider and save first.'
    : !(info.email || info.phone) ? 'No customer email or phone number on file.'
    : informDirty ? 'You have unsaved changes. Save processing info first.'
    : null;
  const informBlocker = baseBlocker
    || (needsAppt && !(savedApptDate && info.appointment_time) ? `Enter and save the ${apptLabels.date.toLowerCase()} and ${apptLabels.time.toLowerCase()} first.` : null)
    || (needsCancelDate && !savedCancelDate ? 'Enter and save the cancellation date first.' : null);
  const fmtMdy = (iso: string) => (iso && iso.length >= 10 ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : iso);
  const informChosen: string = savedProviders.length === 1 ? (savedProviders[0].provider_id || '')
    : (savedProviders.some((p) => p.provider_id === informProvider) ? informProvider : '');
  const informChosenName = savedProviders.find((p) => p.provider_id === informChosen)?.name || '';
  // The chosen provider's own account # (the service(s) it handles), as saved.
  const informAcct = (info.providers || [])
    .filter((p) => p.provider_id === informChosen && p.domain)
    .map((p) => info.provider_accounts?.[p.domain]?.account_number || '')
    .find(Boolean) || '';
  // Sale-type templates show the amount charged; the standard invoice shows amount collected.
  const informAmount = kind ? info.amount : (info.amount_collected != null ? info.amount_collected : info.amount);
  // One contact on file -> that channel is preselected; both on file -> nothing preselected, the processor must choose.
  const hasEmail = !!(info.email || '').trim();
  const hasPhone = !!(info.phone || '').trim();
  const defaultCh = () => ({ email: hasEmail && !hasPhone, sms: hasPhone && !hasEmail });
  const channelPicker = (ch: { email: boolean; sms: boolean }, setCh: (v: { email: boolean; sms: boolean }) => void, busy: boolean) => (
    <div className="ps-inform-channels">
      <div className={`ps-inform-instr${hasEmail && hasPhone && !ch.email && !ch.sms ? ' ps-inform-instr-need' : ''}`}>
        {hasEmail && hasPhone
          ? 'This customer has both an email and a phone number. Choose how to send: tick Email, SMS, or both. Send stays disabled until you choose.'
          : hasEmail ? 'Only an email address is on file, so this will be sent by Email.'
          : 'Only a phone number is on file, so this will be sent by SMS.'}
      </div>
      <label className="ps-inform-ch">
        <input type="checkbox" checked={ch.email} disabled={!hasEmail || busy} onChange={(e) => setCh({ ...ch, email: e.target.checked })} />
        Email <em>{hasEmail ? info.email : '(no email on file)'}</em>
      </label>
      <label className="ps-inform-ch">
        <input type="checkbox" checked={ch.sms} disabled={!hasPhone || busy} onChange={(e) => setCh({ ...ch, sms: e.target.checked })} />
        SMS <em>{hasPhone ? info.phone : '(no phone number on file)'}</em>
      </label>
    </div>
  );

  return (
    <div className="processing-section">
      {error && <div className="ps-error">{error}</div>}
      {!canEdit && <div className="ps-readonly-note">Read-only — processing fields are edited by the processing team.</div>}

      <fieldset className="ps-fieldset" disabled={!canEdit}>
        {/* Pseudo identity: empty until filled in, kept separately from the customer
            identity. Processing team only; TL and above edit it. */}
        {info.pseudo_identity && (() => {
          const p = info.pseudo_identity;
          const editable = !!info.can_edit_pseudo;
          return (
            <Card title="Pseudo identity" defaultOpen={false}
              note={editable ? 'Editable · saved with this panel' : 'Read-only'}>
              {PSEUDO_LAYOUT.map((f) => {
                if (f.kind === 'secret') {
                  return (
                    <Sensitive key={f.key} label={f.label} hasValue={!!p[`has_${f.flag}`]}
                      last4={p[`${f.flag}_last4`] as string | null | undefined}
                      revealed={revealed[`pseudo_${f.flag}`]} input={pSecret[f.key]} readOnly={!editable}
                      error={fieldErrors[`pseudo.${f.key}`]}
                      onInput={(v) => {
                        setPSecret((s) => ({ ...s, [f.key]: PSEUDO_SECRET_CLEAN[f.key](v) }));
                        setFieldErrors((fe) => (fe[`pseudo.${f.key}`] ? { ...fe, [`pseudo.${f.key}`]: '' } : fe));
                      }} />
                  );
                }
                const val = pform[f.key] ?? '';
                if (!editable) return <Ref key={f.key} label={f.label} value={val || '—'} />;
                const setP = (v: string) => setPform((s) => ({ ...s, [f.key]: v }));
                switch (f.kind) {
                  case 'num': return <Num key={f.key} label={f.label} value={val} onChange={setP} />;
                  case 'tri': return <TriSelect key={f.key} label={f.label} value={val as TriState} onChange={setP} />;
                  case 'state': return <Select key={f.key} label={f.label} value={val} onChange={setP} options={US_STATES} />;
                  case 'state_code': return <Select key={f.key} label={f.label} value={val} onChange={setP} options={US_STATE_CODES} />;
                  case 'country': return <Select key={f.key} label={f.label} value={val} onChange={setP} options={COUNTRIES} />;
                  default: return <Text key={f.key} label={f.label} value={val} onChange={setP} placeholder={f.placeholder} maxLength={f.max} />;
                }
              })}
            </Card>
          );
        })()}

        {/* Customer identity */}
        {canEditIdentity ? (
          <Card title="Customer identity" note="Editable · saved with this panel">
            <Text label="First name" value={form.first_name} onChange={(v) => set('first_name', v)} maxLength={64} />
            <Text label="Middle name" value={form.middle_name} onChange={(v) => set('middle_name', v)} maxLength={64} />
            <Text label="Last name" value={form.last_name} onChange={(v) => set('last_name', v)} maxLength={64} />
            <Text label="Company" value={form.company} onChange={(v) => set('company', v)} maxLength={128} />
            <Text label="Date of birth" value={form.date_of_birth} onChange={(v) => set('date_of_birth', v)} placeholder="MM/DD/YYYY" maxLength={10} />
            <Text label="Phone" value={form.phone} onChange={(v) => set('phone', v)} maxLength={32} />
            <Text label="Email" value={form.email} onChange={(v) => set('email', v)} maxLength={256} />
            <Num label="Sales tax" value={form.sales_tax} onChange={(v) => set('sales_tax', v)} />
            <TriSelect label="Tax exempt" value={form.tax_exempt as TriState} onChange={(v) => set('tax_exempt', v)} />
            <Sensitive label="SSN" hasValue={!!info.has_ssn} last4={info.ssn_last4}
              revealed={revealed.ssn} input={ssnInput} readOnly={false}
              onInput={(v) => setSsnInput(v.replace(/[^0-9]/g, '').slice(0, 9))} />
            <Sensitive label="Driver's license #" hasValue={!!info.has_dl} last4={info.dl_last4}
              revealed={decryptedData?.decrypted_card_data?.drivers_license_number} input={dlInput} readOnly={false}
              onInput={(v) => setDlInput(v.replace(/[^A-Za-z0-9]/g, '').slice(0, 32))} />
            <Select label="DL issuing state" value={form.drivers_license_state} onChange={(v) => set('drivers_license_state', v)} options={US_STATE_CODES} />
            <Text label="DL issuing date" value={form.dl_issuing_date} onChange={(v) => set('dl_issuing_date', v)} placeholder="MM/DD/YYYY" maxLength={10} />
            <Text label="DL expiration date" value={form.dl_expiration_date} onChange={(v) => set('dl_expiration_date', v)} placeholder="MM/DD/YYYY" maxLength={10} />
            <Sensitive label="Passport" hasValue={!!info.has_passport} last4={info.passport_last4}
              revealed={revealed.passport} input={passportInput} readOnly={false}
              error={fieldErrors.passport_number}
              onInput={(v) => { setPassportInput(v.replace(/[^A-Za-z0-9]/g, '').slice(0, 12)); setFieldErrors((fe) => (fe.passport_number ? { ...fe, passport_number: '' } : fe)); }} />
            {/* Billing address */}
            <Text label="Billing address 1" value={form.address1} onChange={(v) => set('address1', v)} maxLength={128} />
            <Text label="Billing city" value={form.city} onChange={(v) => set('city', v)} maxLength={64} />
            <Select label="Billing state" value={form.state} onChange={(v) => set('state', v)} options={US_STATES} />
            <Text label="Billing ZIP" value={form.zip} onChange={(v) => set('zip', v)} maxLength={20} />
            <Select label="Billing country" value={form.country} onChange={(v) => set('country', v)} options={COUNTRIES} />
            {/* Service address */}
            <Text label="Service first name" value={form.shipping_firstname} onChange={(v) => set('shipping_firstname', v)} maxLength={64} />
            <Text label="Service last name" value={form.shipping_lastname} onChange={(v) => set('shipping_lastname', v)} maxLength={64} />
            <Text label="Service company" value={form.shipping_company} onChange={(v) => set('shipping_company', v)} maxLength={128} />
            <Text label="Service address 1" value={form.shipping_address_1} onChange={(v) => set('shipping_address_1', v)} maxLength={128} />
            <Text label="Service city" value={form.shipping_city} onChange={(v) => set('shipping_city', v)} maxLength={64} />
            <Select label="Service state" value={form.shipping_state} onChange={(v) => set('shipping_state', v)} options={US_STATES} />
            <Text label="Service ZIP" value={form.shipping_zip} onChange={(v) => set('shipping_zip', v)} maxLength={20} />
            <Select label="Service country" value={form.shipping_country} onChange={(v) => set('shipping_country', v)} options={COUNTRIES} />
          </Card>
        ) : (
          <Card title="Customer identity" note="Read-only from sale · reveal for full values">
            <Ref label="First name" value={info.first_name || '—'} />
            <Ref label="Middle name" value={info.middle_name || '—'} />
            <Ref label="Last name" value={info.last_name || '—'} />
            <Ref label="Date of birth" value={info.date_of_birth || '—'} />
            <Ref label="Phone" value={info.phone || '—'} />
            <Ref label="Email" value={info.email || '—'} full />
            <Ref label="Billing address" value={bill} full />
            <Ref label="Service address" value={service} full />
            <Ref label="SSN" value={ssnDisplay} />
            <Ref label="Driver's license" value={dlDisplay} />
            <Sensitive label="Passport" hasValue={!!info.has_passport} last4={info.passport_last4}
              revealed={revealed.passport} input={passportInput} readOnly={!canEdit}
              error={fieldErrors.passport_number}
              onInput={(v) => { setPassportInput(v.replace(/[^A-Za-z0-9]/g, '').slice(0, 12)); setFieldErrors((fe) => (fe.passport_number ? { ...fe, passport_number: '' } : fe)); }} />
          </Card>
        )}

        {/* Account / provider */}
        <Card title="Account / provider" action={canAddProvider ? <AddProviderButton onAdded={load} /> : undefined}>
          <div className="ps-field full">
            <label>Type of service &amp; provider</label>
            <div className="ps-domains">
              {SERVICE_DOMAINS.map((d) => {
                const checked = domains.includes(d);
                const opts = domainOptions[d] || [];
                const curSel = providerSel[d] || '';
                const curName = (info.providers || []).find((p) => p.provider_id === curSel)?.name;
                const a = acctOf(d);
                return (
                  <div key={d} className={`ps-domain-block${checked ? ' on' : ''}`}>
                    <div className="ps-domain-row">
                      <label className="ps-domain-check">
                        <input type="checkbox" checked={checked}
                          onChange={(e) => setDomains((prev) => (e.target.checked ? [...prev, d] : prev.filter((x) => x !== d)))} />
                        {d}
                      </label>
                      {checked && (
                        <select value={curSel} onChange={(e) => setProviderSel((prev) => ({ ...prev, [d]: e.target.value }))}>
                          <option value="">— Select provider —</option>
                          {opts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          {curSel && !opts.some((p) => p.id === curSel) && (
                            <option value={curSel}>{curName || 'Current provider'}</option>
                          )}
                        </select>
                      )}
                    </div>
                    {/* This service's own utility account details */}
                    {checked && (
                      <div className="ps-acct-grid">
                        <Text label="Account number" value={a.account_number || ''} onChange={(v) => setAcct(d, 'account_number', v)} maxLength={64} />
                        <Text label="Provider phone" value={a.provider_phone || ''} onChange={(v) => setAcct(d, 'provider_phone', v)} maxLength={32} />
                        <Select label="Account status" value={a.provider_account_status || ''} onChange={(v) => setAcct(d, 'provider_account_status', v)} options={[...PROCESSING_ACCOUNT_STATUSES]} />
                        <Text label="Username" value={a.provider_username || ''} onChange={(v) => setAcct(d, 'provider_username', v)} maxLength={128} />
                        <Text label="Password" value={a.provider_password || ''} onChange={(v) => setAcct(d, 'provider_password', v)} maxLength={256} />
                        <Text label="PIN / passphrase" value={a.provider_pin || ''} onChange={(v) => setAcct(d, 'provider_pin', v)} maxLength={128} />
                        <Text label="Guest-pay link" value={a.guest_pay_link || ''} onChange={(v) => setAcct(d, 'guest_pay_link', v)} placeholder="https://…" error={fieldErrors[`${d}.guest_pay_link`]} maxLength={512} />
                        <TriSelect label="Autopay enrolled" value={(a.autopay_enrolled || '') as TriState} onChange={(v) => setAcct(d, 'autopay_enrolled', v)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {needsAppt && (
            <>
              <DateField label={apptLabels.date} value={form.appointment_date} onChange={(v) => set('appointment_date', v)} error={fieldErrors.appointment_date} />
              <AppointmentTimeField label={apptLabels.time} value={form.appointment_time || ''} onChange={(v) => set('appointment_time', v)} />
            </>
          )}
          {needsCancelDate && (
            <DateField label="Cancellation date" value={form.cancellation_date} onChange={(v) => set('cancellation_date', v)} error={fieldErrors.cancellation_date} />
          )}
          {/* Inform Customer: sale-type confirmation as the provider, once only
              (hidden while the server switch INFORM_CUSTOMER_ENABLED is off) */}
          {info.inform_enabled && <div className="ps-field full ps-inform">
            <label>Inform customer{kind ? ` (${KIND_LABEL[kind]})` : ''}</label>
            {info.customer_informed_at && !info.allow_resend ? (
              <div className="ps-inform-done">
                Sent as <strong>{info.customer_informed_provider || 'provider'}</strong>
                {info.customer_informed_via ? ` via ${info.customer_informed_via === 'both' ? 'Email + SMS' : info.customer_informed_via.toUpperCase()}` : ''}
                {info.customer_informed_by_name ? ` by ${info.customer_informed_by_name}` : ''}
                {` · ${new Date(info.customer_informed_at).toLocaleString()}`}
              </div>
            ) : !informOpen ? (
              <div className="ps-inform-row">
                <button type="button" className="ps-inform-btn" disabled={!!informBlocker}
                  onClick={() => { setInformErr(null); setInformCh(defaultCh()); setInformOpen(true); }}>
                  {info.customer_informed_at ? 'Send again' : 'Inform Customer'}
                </button>
                <span className="ps-inform-hint">{informBlocker || (info.allow_resend
                  ? (info.customer_informed_at
                    ? `Last sent as ${info.customer_informed_provider || 'provider'}${info.customer_informed_by_name ? ` by ${info.customer_informed_by_name}` : ''} · ${new Date(info.customer_informed_at).toLocaleString()}. Testing mode: can be sent again.`
                    : `Sends the ${kind ? KIND_LABEL[kind].toLowerCase() : 'payment confirmation'} email and SMS as the provider. Testing mode: can be sent repeatedly.`)
                  : `Sends the ${kind ? KIND_LABEL[kind].toLowerCase() : 'payment confirmation'} email and SMS as the provider. Can be sent only once.`)}</span>
              </div>
            ) : (
              <div className="ps-inform-confirm">
                {savedProviders.length > 1 && (
                  <select value={informChosen} onChange={(e) => setInformProvider(e.target.value)} disabled={informing}>
                    <option value="">Choose provider to send as…</option>
                    {savedProviders.map((p) => <option key={p.provider_id || ''} value={p.provider_id || ''}>{p.name}</option>)}
                  </select>
                )}
                <div className="ps-inform-preview">
                  <div><span>Email</span><strong>{kind ? KIND_LABEL[kind] : 'Payment confirmation'}</strong></div>
                  <div><span>Provider</span><strong>{informChosenName || '—'}</strong></div>
                  <div><span>Account #</span><strong>{informAcct || '—'}</strong>
                    {!informAcct && <em> (not included)</em>}</div>
                  <div><span>Amount</span><strong>{informAmount != null ? `$${Number(informAmount).toFixed(2)}` : '—'}</strong>
                    {!kind && info.amount_collected == null && <em> (amount charged; no amount collected entered)</em>}</div>
                  {(kind === 'bill' || !kind) && (
                    <div><span>Confirmation #</span><strong>{info.confirmation_number || '—'}</strong>
                      {!info.confirmation_number && <em>{kind === 'bill' ? ' (a random number will be generated and saved)' : ' (not included)'}</em>}</div>
                  )}
                  {needsAppt && (
                    <>
                      <div><span>{apptLabels.date}</span><strong>{fmtMdy(savedApptDate)}</strong></div>
                      <div><span>{apptLabels.time}</span><strong>{info.appointment_time}</strong></div>
                    </>
                  )}
                  {needsCancelDate && (
                    <div><span>Cancellation date</span><strong>{fmtMdy(savedCancelDate)}</strong></div>
                  )}
                </div>
                {channelPicker(informCh, setInformCh, informing)}
                <div className="ps-inform-warn">{info.allow_resend ? 'Testing mode: this can be sent again.' : 'This can be sent only once. Check the details before sending.'}</div>
                {informErr && <div className="ps-error">{informErr}</div>}
                <div className="ps-inform-row">
                  <button type="button" className="ps-inform-btn" disabled={informing || !informChosen || !(informCh.email || informCh.sms)}
                    onClick={() => handleInform(informChosen)}>
                    {informing ? 'Sending…' : 'Send to customer'}
                  </button>
                  <button type="button" className="ps-inform-cancel" disabled={informing} onClick={() => setInformOpen(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>}
          {/* New sales: deposit confirmation, a separate once-only send with the upload link
              (hidden while the server switch DEPOSIT_CONFIRMATION_ENABLED is off) */}
          {isNew && info.deposit_enabled && (
            <div className="ps-field full ps-inform">
              <label>Deposit confirmation</label>
              {info.deposit_informed_at && !info.allow_resend ? (
                <div className="ps-inform-done">
                  Sent as <strong>{info.deposit_informed_provider || 'provider'}</strong>
                  {info.deposit_informed_via ? ` via ${info.deposit_informed_via === 'both' ? 'Email + SMS' : info.deposit_informed_via.toUpperCase()}` : ''}
                  {info.deposit_informed_by_name ? ` by ${info.deposit_informed_by_name}` : ''}
                  {` · ${new Date(info.deposit_informed_at).toLocaleString()}`}
                </div>
              ) : !depositOpen ? (
                <div className="ps-inform-row">
                  <button type="button" className="ps-inform-btn" disabled={!!baseBlocker}
                    onClick={() => { setDepositErr(null); setDepositCh(defaultCh()); setDepositOpen(true); }}>
                    {info.deposit_informed_at ? 'Send deposit confirmation again' : 'Send deposit confirmation'}
                  </button>
                  <span className="ps-inform-hint">{baseBlocker || (info.allow_resend
                    ? (info.deposit_informed_at
                      ? `Last sent as ${info.deposit_informed_provider || 'provider'}${info.deposit_informed_by_name ? ` by ${info.deposit_informed_by_name}` : ''} · ${new Date(info.deposit_informed_at).toLocaleString()}. Testing mode: can be sent again.`
                      : 'Sends the deposit-received email and SMS as the provider, with the document upload link. Testing mode: can be sent repeatedly.')
                    : 'Sends the deposit-received email and SMS as the provider, with the document upload link. Can be sent only once.')}</span>
                </div>
              ) : (
                <div className="ps-inform-confirm">
                  {savedProviders.length > 1 && (
                    <select value={informChosen} onChange={(e) => setInformProvider(e.target.value)} disabled={depositing}>
                      <option value="">Choose provider to send as…</option>
                      {savedProviders.map((p) => <option key={p.provider_id || ''} value={p.provider_id || ''}>{p.name}</option>)}
                    </select>
                  )}
                  <div className="ps-inform-preview">
                    <div><span>Email</span><strong>Security deposit received</strong></div>
                    <div><span>Provider</span><strong>{informChosenName || '—'}</strong></div>
                    <div><span>Deposit amount</span><strong>{info.amount != null ? `$${Number(info.amount).toFixed(2)}` : '—'}</strong></div>
                    <div><span>Upload link</span><strong>Included</strong><em> (reuses the active document link, or creates one)</em></div>
                  </div>
                  {channelPicker(depositCh, setDepositCh, depositing)}
                  <div className="ps-inform-warn">{info.allow_resend ? 'Testing mode: this can be sent again.' : 'This can be sent only once. Check the details before sending.'}</div>
                  {depositErr && <div className="ps-error">{depositErr}</div>}
                  <div className="ps-inform-row">
                    <button type="button" className="ps-inform-btn" disabled={depositing || !informChosen || !(depositCh.email || depositCh.sms)}
                      onClick={() => handleDeposit(informChosen)}>
                      {depositing ? 'Sending…' : 'Send to customer'}
                    </button>
                    <button type="button" className="ps-inform-cancel" disabled={depositing} onClick={() => setDepositOpen(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Billing snapshot */}
        <Card title="Billing snapshot">
          <Num label="Total bill amount" value={form.total_bill_amount} onChange={(v) => set('total_bill_amount', v)} error={fieldErrors.total_bill_amount} />
          <Num label="Past due amount" value={form.past_due_amount} onChange={(v) => set('past_due_amount', v)} error={fieldErrors.past_due_amount} />
          <Num label="Overdue balance" value={form.overdue_balance} onChange={(v) => set('overdue_balance', v)} error={fieldErrors.overdue_balance} />
          <DateField label="Next bill date" value={form.next_bill_date} onChange={(v) => set('next_bill_date', v)} error={fieldErrors.next_bill_date} />
        </Card>

        {/* Payment to utility */}
        <Card title="Payment to utility">
          <Ref label="Agent" value={info.created_by_name || '—'} />
          <Ref label="Customer charged" value={info.amount != null ? `$${info.amount.toFixed(2)}` : '—'} />
          <Num label="Actual amount collected" value={form.amount_collected} onChange={(v) => set('amount_collected', v)} error={fieldErrors.amount_collected} />
          <Select label="Payment method" value={form.payment_method} onChange={(v) => set('payment_method', v)} options={[...PROCESSING_PAYMENT_METHODS]} />
          <div className="ps-field">
            <label>Payment status</label>
            <select value={form.utility_payment_status} onChange={(e) => set('utility_payment_status', e.target.value)}>
              <option value="">—</option>
              {form.utility_payment_status && !PROCESSING_UTILITY_PAYMENT_STATUSES.includes(form.utility_payment_status as any) && (
                <option value={form.utility_payment_status}>{form.utility_payment_status} (current)</option>
              )}
              {PROCESSING_UTILITY_PAYMENT_STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <Text label="Confirmation #" value={form.confirmation_number} onChange={(v) => set('confirmation_number', v)} maxLength={128} />
          <div className="ps-field">
            <label>Date/Time paid</label>
            <input type="datetime-local" className={fieldErrors.paid_at ? 'ps-input-error' : ''} value={form.paid_at} onChange={(e) => set('paid_at', e.target.value)} />
            {fieldErrors.paid_at && <span className="ps-field-error">{fieldErrors.paid_at}</span>}
          </div>
          <DateField label="Reversal date" value={form.reversal_date} onChange={(v) => set('reversal_date', v)} error={fieldErrors.reversal_date} />
          {isProcessingRole && (
            <div className="ps-field full ps-proc-comments">
              <label>Processing comments</label>
              <TransactionComments recordId={saleId} scope="processing" />
            </div>
          )}
        </Card>

        {/* Case outcome */}
        <Card title="Sale / case outcome">
          <div className="ps-field">
            <label>Sale status (processor)</label>
            <select value={form.processor_sale_status} onChange={(e) => set('processor_sale_status', e.target.value)}>
              <option value="">—</option>
              {form.processor_sale_status && !PROCESSING_SALE_STATUSES.some((o) => o.value === form.processor_sale_status) && (
                <option value={form.processor_sale_status}>{form.processor_sale_status.replace(/_/g, ' ')} (current)</option>
              )}
              {PROCESSING_SALE_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <TriSelect label="Agent call flag" value={form.agent_call_flag as TriState} onChange={(v) => set('agent_call_flag', v)} />
        </Card>
      </fieldset>

      {canEdit && (
        <div className="ps-actions">
          {savedMsg && <span className="ps-saved">{savedMsg}</span>}
          <button type="button" className="ps-btn" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save processing info'}
          </button>
        </div>
      )}

      <style>{`
        .processing-section { display: flex; flex-direction: column; gap: 10px; }
        .ps-readonly-note { font-size: 0.78rem; color: var(--text-secondary); background: var(--bg-secondary, transparent); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border-primary); }
        .ps-fieldset { border: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
        .ps-card { background: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 10px; padding: 10px 14px; }
        .ps-card-head { position: relative; display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 28px; }
        .ps-card-head + .ps-grid { margin-top: 10px; }
        .ps-caret { font-size: 0.7rem; color: var(--text-secondary); }
        .ps-card-title { font-size: 1.15rem; font-weight: 700; color: var(--text-primary); text-align: center; }
        .ps-card-note { position: absolute; right: 0; top: 50%; transform: translateY(-50%); font-size: 0.7rem; color: var(--text-secondary); max-width: 40%; text-align: right; }
        .ps-card-action { position: absolute; right: 0; top: 50%; transform: translateY(-50%); }
        .ps-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 0.7rem 1rem; align-items: start; }
        .ps-field, .ps-ref { display: flex; flex-direction: column; gap: 3px; min-width: 0; text-align: left; }
        .ps-field.full, .ps-ref.full { grid-column: 1 / -1; }
        .ps-field label, .ps-ref label { font-size: 0.7rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: .02em; line-height: 1.25; }
        .ps-field input, .ps-field select { width: 100%; box-sizing: border-box; height: 32px; padding: 6px 8px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; font-size: 0.8125rem; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); }
        .ps-field input:focus, .ps-field select:focus { outline: none; border-color: var(--input-focus, var(--primary)); }
        .ps-field input:disabled, .ps-field select:disabled { opacity: .7; cursor: default; }
        .ps-field input.ps-input-error { border-color: var(--danger); }
        .ps-field-error { font-size: 0.7rem; color: var(--danger); }
        .ps-ref span { font-size: 0.8125rem; color: var(--text-primary); word-break: break-word; }
        .ps-sensitive-status { font-size: 0.7rem; color: var(--text-secondary); }
        .ps-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; }
        .ps-btn { background: var(--primary); color: var(--text-inverse, #fff); border: none; padding: 9px 16px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .ps-btn:hover { background: var(--primary-hover, var(--primary)); }
        .ps-btn:disabled { opacity: .6; cursor: default; }
        .ps-saved { color: var(--success); font-size: 0.8125rem; font-weight: 600; }
        .ps-error { color: var(--danger); font-size: 0.8125rem; }
        .ps-muted { color: var(--text-secondary); font-size: 0.8125rem; }
        .ps-link { background: none; border: 1px solid var(--primary); color: var(--primary); cursor: pointer; font-size: 0.72rem; padding: 4px 10px; border-radius: 6px; }
        .ps-domains { display: flex; flex-direction: column; gap: 8px; }
        .ps-domain-block.on { padding: 10px 12px; border: 1px solid var(--border-primary); border-radius: 8px; }
        .ps-acct-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 0.7rem 1rem; margin-top: 10px; }
        .ps-acct-grid select { width: 100%; min-width: 0; }
        .ps-domain-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ps-domain-check { display: flex; align-items: center; gap: 6px; font-size: 0.8125rem; color: var(--text-primary); min-width: 130px; text-transform: none; letter-spacing: 0; font-weight: 500; }
        .ps-domain-check input { width: auto; height: auto; }
        .ps-domain-row select { width: auto; min-width: 220px; height: 32px; padding: 6px 8px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; font-size: 0.8125rem; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); }
        .ps-proc-comments { margin-top: 6px; padding-top: 12px; border-top: 1px dashed var(--border-primary); }
        .ps-recon-window { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
        .ps-recon-window select { flex: 1; min-width: 0; }
        .ps-recon-window span { font-size: 0.75rem; color: var(--text-secondary); }
        .ps-inform { margin-top: 6px; padding-top: 12px; border-top: 1px dashed var(--border-primary); }
        .ps-inform-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ps-inform-btn { background: var(--primary); color: var(--text-inverse, #fff); border: none; padding: 8px 14px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .ps-inform-btn:disabled { opacity: .55; cursor: default; }
        .ps-inform-cancel { background: none; color: var(--text-primary); border: 1px solid var(--border-primary); padding: 7px 12px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .ps-inform-hint { font-size: 0.78rem; color: var(--text-secondary); }
        .ps-inform-done { font-size: 0.8125rem; color: var(--success); font-weight: 500; }
        .ps-inform-confirm { display: flex; flex-direction: column; gap: 10px; background: var(--bg-secondary, var(--bg-card)); border: 1px solid var(--border-primary); border-radius: 8px; padding: 12px; }
        .ps-inform-confirm select { max-width: 320px; }
        .ps-inform-preview { display: flex; flex-direction: column; gap: 4px; font-size: 0.8125rem; color: var(--text-primary); }
        .ps-inform-preview span { display: inline-block; min-width: 120px; color: var(--text-secondary); }
        .ps-inform-preview em { color: var(--text-secondary); font-size: 0.75rem; }
        .ps-inform-channels { display: flex; flex-direction: column; gap: 6px; padding: 10px; border: 1px solid var(--border-primary); border-radius: 6px; background: var(--bg-card); }
        .ps-inform-instr { font-size: 0.8rem; color: var(--text-primary); font-weight: 600; }
        .ps-inform-instr-need { color: var(--warning, #b45309); }
        .ps-inform-ch { display: flex; align-items: center; gap: 8px; font-size: 0.8125rem; color: var(--text-primary); text-transform: none; letter-spacing: 0; font-weight: 500; }
        .ps-inform-ch input { width: auto; height: auto; }
        .ps-inform-ch em { color: var(--text-secondary); font-style: normal; }
        .ps-inform-warn { font-size: 0.78rem; font-weight: 600; color: var(--warning, #b45309); }
      `}</style>
    </div>
  );
};

// --- layout + field helpers -------------------------------------------------
const Card: React.FC<{ title: string; note?: string; action?: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, note, action, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ps-card">
      <div className="ps-card-head" onClick={() => setOpen((o) => !o)} style={{ cursor: 'pointer' }}>
        <span className="ps-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="ps-card-title">{title}</span>
        {note && <span className="ps-card-note">{note}</span>}
        {action && <span className="ps-card-action" onClick={(e) => e.stopPropagation()}>{action}</span>}
      </div>
      {open && <div className="ps-grid">{children}</div>}
    </div>
  );
};
const Ref: React.FC<{ label: string; value: string; full?: boolean }> = ({ label, value, full }) => (
  <div className={`ps-ref${full ? ' full' : ''}`}><label>{label}</label><span>{value}</span></div>
);
const Text: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string; error?: string; maxLength?: number }> = ({ label, value, onChange, placeholder, error, maxLength }) => (
  <div className="ps-field">
    <label>{label}</label>
    <input type="text" className={error ? 'ps-input-error' : ''} value={value} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
    {error && <span className="ps-field-error">{error}</span>}
  </div>
);
const Num: React.FC<{ label: string; value: string; onChange: (v: string) => void; error?: string }> = ({ label, value, onChange, error }) => (
  <div className="ps-field">
    <label>{label}</label>
    <input type="text" inputMode="decimal" className={error ? 'ps-input-error' : ''} value={value} onChange={(e) => onChange(sanitizeMoney(e.target.value))} />
    {error && <span className="ps-field-error">{error}</span>}
  </div>
);
const DateField: React.FC<{ label: string; value: string; onChange: (v: string) => void; error?: string }> = ({ label, value, onChange, error }) => (
  <div className="ps-field">
    <label>{label}</label>
    <input type="date" className={error ? 'ps-input-error' : ''} value={value} onChange={(e) => onChange(e.target.value)} />
    {error && <span className="ps-field-error">{error}</span>}
  </div>
);
// Appointment window (Restore / New / Transfer), stored as display text (no time zone):
// "All day (8:00 AM - 8:00 PM)" or "H:MM AM - H:MM PM" in 30-min steps within
// 8:00 AM - 8:00 PM, end after start. Mirrors the backend validator.
const APPT_ALL_DAY = 'All day (8:00 AM - 8:00 PM)';
const APPT_SLOTS = Array.from({ length: 25 }, (_, i) => 8 * 60 + i * 30); // 8:00 AM .. 8:00 PM
const fmtSlot = (min: number): string => {
  const h24 = Math.floor(min / 60), mm = min % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm === 0 ? '00' : '30'} ${h24 < 12 ? 'AM' : 'PM'}`;
};
const parseSlot = (s: string): number | null => {
  const m = /^(\d{1,2}):(\d{2}) (AM|PM)$/.exec(s.trim());
  if (!m) return null;
  return ((Number(m[1]) % 12) + (m[3] === 'PM' ? 12 : 0)) * 60 + Number(m[2]);
};
const AppointmentTimeField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
  const parts = value && value !== APPT_ALL_DAY ? value.split(' - ') : [];
  const savedStart = parts.length === 2 ? parseSlot(parts[0]) : null;
  const savedEnd = parts.length === 2 ? parseSlot(parts[1]) : null;
  const [mode, setMode] = useState<'' | 'allday' | 'window'>(value === APPT_ALL_DAY ? 'allday' : savedStart != null ? 'window' : '');
  const [start, setStart] = useState<number | null>(savedStart);
  const [end, setEnd] = useState<number | null>(savedEnd);

  // Re-sync when the saved value changes underneath (load / save / reload).
  useEffect(() => {
    if (value === APPT_ALL_DAY) { setMode('allday'); return; }
    const p = value ? value.split(' - ') : [];
    const s = p.length === 2 ? parseSlot(p[0]) : null;
    const e = p.length === 2 ? parseSlot(p[1]) : null;
    if (s != null && e != null) { setMode('window'); setStart(s); setEnd(e); }
    else if (!value) { setMode((m) => (m === 'window' ? m : '')); }
  }, [value]);

  const emit = (m: string, s: number | null, e: number | null) => {
    if (m === 'allday') onChange(APPT_ALL_DAY);
    else if (m === 'window' && s != null && e != null && e > s) onChange(`${fmtSlot(s)} - ${fmtSlot(e)}`);
    else onChange('');   // incomplete window: nothing valid to save yet
  };

  return (
    <div className="ps-field">
      <label>{label}</label>
      <select value={mode} onChange={(e) => { const m = e.target.value as typeof mode; setMode(m); emit(m, start, end); }}>
        <option value="">—</option>
        <option value="allday">{APPT_ALL_DAY}</option>
        <option value="window">Time window</option>
      </select>
      {mode === 'window' && (
        <div className="ps-recon-window">
          <select value={start ?? ''} onChange={(e) => {
            const s = e.target.value === '' ? null : Number(e.target.value);
            const en = end != null && s != null && end <= s ? null : end;
            setStart(s); setEnd(en); emit('window', s, en);
          }}>
            <option value="">Start</option>
            {APPT_SLOTS.slice(0, -1).map((t) => <option key={t} value={t}>{fmtSlot(t)}</option>)}
          </select>
          <span>to</span>
          <select value={end ?? ''} onChange={(e) => {
            const en = e.target.value === '' ? null : Number(e.target.value);
            setEnd(en); emit('window', start, en);
          }}>
            <option value="">End</option>
            {APPT_SLOTS.filter((t) => start == null || t > start).slice(start == null ? 1 : 0).map((t) => <option key={t} value={t}>{fmtSlot(t)}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};
const Select: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: readonly string[]; noBlank?: boolean }> = ({ label, value, onChange, options, noBlank }) => (
  <div className="ps-field">
    <label>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {!noBlank && <option value="">—</option>}
      {/* A saved value outside the list (old free-text entry) stays visible until changed. */}
      {value && !options.includes(value) && <option value={value}>{value} (current)</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const TriSelect: React.FC<{ label: string; value: TriState; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div className="ps-field">
    <label>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  </div>
);
const Sensitive: React.FC<{
  label: string; hasValue: boolean; last4?: string | null; revealed?: string;
  input: string; onInput: (v: string) => void; readOnly?: boolean; error?: string;
}> = ({ label, hasValue, last4, revealed, input, onInput, readOnly, error }) => (
  <div className="ps-field">
    <label>{label}</label>
    {!readOnly && (
      <input type="text" className={error ? 'ps-input-error' : ''} value={input}
        placeholder={hasValue ? 'Enter new value to replace' : 'Enter value'} onChange={(e) => onInput(e.target.value)} />
    )}
    {error
      ? <span className="ps-field-error">{error}</span>
      : <span className="ps-sensitive-status">{revealed != null ? revealed : hasValue ? (last4 ? `Current: •••• ${last4}` : 'Set') : 'Not set'}</span>}
  </div>
);

// --- Add-provider modal -----------------------------------------------------
const AddProviderButton: React.FC<{ onAdded: () => void }> = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState(SERVICE_DOMAINS[0]);
  const [description, setDescription] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // Set when the provider was created but its logo upload failed, so the retry
  // only re-uploads the logo (processor TLs can't reach Settings to fix it later).
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const finish = () => {
    setOpen(false);
    setName(''); setDescription(''); setLogoFile(null); setCreatedId(null); setErr(null);
    onAdded();
  };

  const submit = async () => {
    if (!createdId && !name.trim()) { setErr('Name is required'); return; }
    setBusy(true);
    setErr(null);
    try {
      let id = createdId;
      if (!id) {
        const created = await SettingsAPI.createProvider({ name: name.trim(), domain, description: description.trim() || undefined });
        id = created?.id || null;
      }
      if (logoFile && id) {
        try {
          await SettingsAPI.uploadProviderLogo(id, logoFile);
        } catch (e: any) {
          setCreatedId(id);
          setErr(`Provider added, but the logo didn't upload: ${e?.message || 'unknown error'}. Retry, or skip the logo.`);
          return;
        }
      }
      finish();
    } catch (e: any) {
      setErr(e?.message || 'Failed to add provider');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="ps-link" onClick={() => setOpen(true)}>+ Add provider</button>
      {open && (
        <div className="ps-modal-overlay" onClick={() => !busy && (createdId ? finish() : setOpen(false))}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Add provider</h4>
            {err && <div className="ps-error">{err}</div>}
            <label>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus disabled={!!createdId} />
            <label>Type of service</label>
            <select value={domain} onChange={(e) => setDomain(e.target.value)} disabled={!!createdId}>
              {SERVICE_DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <label>Description (optional)</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} disabled={!!createdId} />
            <div className="ps-modal-logo">
              <LogoPicker file={logoFile} onChange={setLogoFile} disabled={busy} />
            </div>
            <div className="ps-modal-actions">
              {createdId ? (
                <>
                  <button type="button" className="ps-btn-secondary" disabled={busy} onClick={finish}>Skip logo</button>
                  <button type="button" className="ps-btn" disabled={busy || !logoFile} onClick={submit}>{busy ? 'Uploading…' : 'Retry logo'}</button>
                </>
              ) : (
                <>
                  <button type="button" className="ps-btn-secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
                  <button type="button" className="ps-btn" disabled={busy} onClick={submit}>{busy ? 'Adding…' : 'Add'}</button>
                </>
              )}
            </div>
          </div>
          <style>{`
            .ps-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1200; }
            .ps-modal { background: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-primary); border-radius: 12px; padding: 20px; width: 380px; max-width: 92vw; display: flex; flex-direction: column; gap: 6px; }
            .ps-modal h4 { margin: 0 0 8px; font-size: 1rem; color: var(--text-primary); }
            .ps-modal label { font-size: 0.72rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; margin-top: 8px; }
            .ps-modal input, .ps-modal select { padding: 8px 10px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; font-size: 0.8125rem; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); }
            .ps-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
            .ps-modal-logo { margin-top: 10px; }
            .ps-modal-logo .logo-picker-label { text-transform: uppercase; font-size: 0.72rem; margin-top: 0; }
            .ps-btn-secondary { background: var(--bg-tertiary, var(--bg-secondary)); color: var(--text-primary); border: 1px solid var(--border-primary); padding: 9px 16px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
          `}</style>
        </div>
      )}
    </>
  );
};
